/**
 * @file Tiny `localStorage` read/write guard.
 *
 * Some browser configurations (strict privacy mode, storage quota exceeded)
 * throw on `localStorage` access rather than returning `null`, so every call
 * site would otherwise need its own try/catch. Two call sites already share
 * this exact guard (music volume, SFX volume), which is what earns it a
 * shared helper instead of inlining it twice.
 */

/**
 * @param {string} key Storage key.
 * @param {number} fallback Value returned when the key is missing or invalid.
 * @returns {number}
 */
export function loadNumber(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * @param {string} key Storage key.
 * @param {number} value Value to persist.
 * @returns {void}
 */
export function saveNumber(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Storage unavailable (privacy mode, quota); the setting just won't persist.
  }
}
